import classNames from 'classnames/bind';
import { PropsWithChildren, ReactElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Dialog } from '@headlessui/react';

import { Body } from '@/components/shared/typography/Body';
import { Button, ButtonIcon, ButtonProps } from '@/components/shared/buttons';
import { HeaderVariants, Header as TypographyHeader } from '@/components/shared/typography/Header';
import { ContainerLoader } from '@/components/shared/loaders/ContainerLoader';

import style from './Modal.module.sass';
import {
  modal_overlay,
  modal_panel,
} from './ModalAnimationFlows';

const cx = classNames.bind(style);

type ModalProps = PropsWithChildren & {
  isOpen: boolean;
  handleCancel: () => void;
  variant?: 'default' | 'medium' | 'fullscreen' | 'custom';
  className?: string;
  isLoading?: boolean;
  /**
   * Use this when migrating old context-driven modals to localized state.
   * Keep modal data mounted while closing by setting isOpen=false, then clear
   * that data in onExited. Clearing data directly in handleCancel unmounts this
   * component immediately and skips the Framer Motion exit animation.
   */
  onExited?: () => void;
};

export const Modal = ({
  children,
  isOpen,
  handleCancel,
  variant = 'default',
  className,
  isLoading,
  onExited,
}: ModalProps) => {
  const panelClassNames = cx('panel', variant, className);
  const panelAnimationType = variant === 'default'
    ? modal_panel
    : modal_overlay;

  return (
    <AnimatePresence onExitComplete={onExited}>
      {
        isOpen &&
        <Dialog
          key='modal'
          aria-label='modal'
          data-testid='modal'
          open={true}
          onClose={handleCancel}>
          <motion.div
            aria-hidden='true'
            className={style.backdrop}
            {...modal_overlay}
          />
          {isLoading ? (
            <div className={style.loadingWrapper}>
              <ContainerLoader />
            </div>
          ) : (
            <Dialog.Panel
              as={motion.div}
              className={panelClassNames}
              {...panelAnimationType}>
              {children}
            </Dialog.Panel>
          )}
        </Dialog>
      }
    </AnimatePresence>
  );
};

type HeaderProps = {
  title: ReactNode;
  icon?: ReactElement;
  subtitle?: ReactNode;
  handleCancel?: () => void;
  headerVariant?: HeaderVariants;
};

const Header = ({
  icon,
  title,
  subtitle,
  handleCancel,
  headerVariant = 'h4',
}: HeaderProps) => {
  return (
    <div className={style.header}>
      {
        icon &&
        <div className={style.headerIcon}>
          {icon}
        </div>
      }
      <div className={style.titleWrapper}>
        <TypographyHeader
          variant={headerVariant}
          className={style.header}>
          {title}
        </TypographyHeader>
        {
          subtitle &&
          <Body
            className={style.subtitle}
            size='base'>
            {subtitle}
          </Body>
        }
      </div>
      <div className={style.closeButton}>
        <ButtonIcon
          onClick={handleCancel}
          iconName='Delete'
        />
      </div>
    </div>
  );
};

type FooterProps = PropsWithChildren;

const Footer = ({
  children,
}: FooterProps) => {
  return (
    <div className={style.footer}>
      {children}
    </div>
  );
};

type CancelButtonProps = PropsWithChildren<{
  onClick: () => void;
  buttonWrapperClassName?: string;
  variant?: 'primary' | 'secondary';
  sizeVariant?: ButtonProps['sizeVariant'];
}>;

const CancelButton = ({
  children,
  variant = 'secondary',
  onClick,
  buttonWrapperClassName,
  sizeVariant = 'small',
}: CancelButtonProps) => {
  const { t } = useTranslation();

  const buttonWrapperClassNames = cx('buttonWrapper', buttonWrapperClassName);

  return (
    <div className={buttonWrapperClassNames}>
      <Button
        variant={variant}
        onClick={onClick}
        width='full'
        sizeVariant={sizeVariant}>
        {children || t('common:cancel')}
      </Button>
    </div>
  );
};

Modal.Header = Header;
Modal.Footer = Footer;
Modal.CancelButton = CancelButton;
